/**
 * Client Error Reporter
 * ---------------------
 * Minimaler Frontend-Error-Sink. Schreibt strukturierte Fehler über die
 * SECURITY DEFINER RPC `log_client_error` in `public.client_error_logs`.
 *
 * Schutzmaßnahmen (Best-Effort, da keine Backend-Rate-Limits):
 *  - Client-seitiges Throttling pro Fingerprint (10s)
 *  - Hartes Session-Cap (50 Reports / Session)
 *  - Stack-Truncation
 *  - Ignorierte Noise-Patterns (Browser-Extensions, ResizeObserver-Loop)
 *
 * Bewusst ohne Edge Function: die RPC ist anon-callable und enthält bereits
 * Whitelist-Validierung sowie ein 100/h-Soft-Cap pro Session.
 */

import { supabase } from '@/integrations/supabase/client';

type ErrorSource = 'window_error' | 'unhandled_rejection' | 'react_boundary' | 'manual';
type ErrorSeverity = 'warning' | 'error' | 'fatal';

interface ReportInput {
  message: string;
  stack?: string | null;
  source?: ErrorSource;
  severity?: ErrorSeverity;
  context?: Record<string, unknown> | null;
}

const SESSION_CAP = 50;
const THROTTLE_MS = 10_000;

// Hartnäckiger, harmloser Browser-Noise — nicht ins Monitoring lassen.
const IGNORE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/i,
  /Loading chunk \d+ failed/i, // dynamic-import retries handled separately
];

let installed = false;
let reportCount = 0;
const lastSentByFingerprint = new Map<string, number>();

function getOrCreateSessionHash(): string {
  if (typeof window === 'undefined') return 'ssr';
  const KEY = 'scuvio.monitor.session.v1';
  try {
    let v = window.sessionStorage.getItem(KEY);
    if (!v) {
      v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return 'no-storage';
  }
}

function getAppVersion(): string {
  // Vite injects via define() in vite.config; fall back to commit-less marker.
  const v =
    (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_APP_VERSION) ||
    'dev';
  return String(v).slice(0, 100);
}

function fingerprintOf(message: string, stack?: string | null): string {
  const head = (stack ?? '').split('\n').slice(0, 3).join('|');
  // Stable, low-cardinality bucket. md5 happens server-side as fallback.
  return `${message.slice(0, 120)}::${head.slice(0, 200)}`;
}

function shouldIgnore(message: string): boolean {
  return IGNORE_PATTERNS.some((re) => re.test(message));
}

function throttled(fingerprint: string): boolean {
  const now = Date.now();
  const last = lastSentByFingerprint.get(fingerprint) ?? 0;
  if (now - last < THROTTLE_MS) return true;
  lastSentByFingerprint.set(fingerprint, now);
  // Trim map to keep memory bounded.
  if (lastSentByFingerprint.size > 200) {
    const oldest = [...lastSentByFingerprint.entries()].sort((a, b) => a[1] - b[1])[0]?.[0];
    if (oldest) lastSentByFingerprint.delete(oldest);
  }
  return false;
}

/**
 * Public API: report a single client error.
 * Returns silently on any failure — monitoring must never break the app.
 */
export async function reportClientError(input: ReportInput): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (reportCount >= SESSION_CAP) return;

    const message = (input.message ?? '').toString().trim();
    if (!message) return;
    if (shouldIgnore(message)) return;

    const fingerprint = fingerprintOf(message, input.stack ?? null);
    if (throttled(fingerprint)) return;

    reportCount += 1;

    await supabase.rpc('log_client_error', {
      p_message: message,
      p_stack: input.stack ?? null,
      p_source: input.source ?? 'manual',
      p_severity: input.severity ?? 'error',
      p_url: window.location?.href ?? null,
      p_user_agent: window.navigator?.userAgent ?? null,
      p_app_version: getAppVersion(),
      p_session_hash: getOrCreateSessionHash(),
      p_context: (input.context ?? null) as never,
      p_fingerprint: fingerprint,
    });
  } catch {
    // Swallow — monitoring is best-effort.
  }
}

/**
 * Install global handlers. Idempotent — safe to call multiple times.
 * Must only run in browser. Called once from `main.tsx`.
 */
export function installClientErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    void reportClientError({
      message: ev.message || ev.error?.message || 'Unknown window error',
      stack: ev.error?.stack ?? null,
      source: 'window_error',
      severity: 'error',
      context: {
        filename: ev.filename ?? null,
        lineno: ev.lineno ?? null,
        colno: ev.colno ?? null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : (() => {
              try {
                return JSON.stringify(reason);
              } catch {
                return 'Unknown promise rejection';
              }
            })();
    void reportClientError({
      message: `Unhandled rejection: ${message}`,
      stack: reason instanceof Error ? reason.stack ?? null : null,
      source: 'unhandled_rejection',
      severity: 'error',
    });
  });
}

// Test/diagnostic exports (not part of the public API).
export const __test__ = {
  resetState() {
    installed = false;
    reportCount = 0;
    lastSentByFingerprint.clear();
  },
  shouldIgnore,
  fingerprintOf,
};
